
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";

import {
  HiOutlineBookOpen,
  HiOutlineArrowDown,
  HiOutlineSquares2X2,
  HiOutlineDocumentText,
} from "react-icons/hi2";

import { Header, Fotter } from "../components/layout";
import { Loader, BlogCategoryCard } from "../components/utils";

import { getAllCategorys } from "../redux/actions/category";
import { BlogsCategoryImage } from "../assets";

/* ============================================
   BLOG CATEGORIES PAGE
============================================ */

function BlogsCategory() {
  const dispatch = useDispatch();
  const { i18n, t } = useTranslation();

  const { isLoading, AllCategorys } = useSelector(
    (state) => state.Categorys || {}
  );

  const isEnglish = (
    i18n.resolvedLanguage ||
    i18n.language ||
    "ar"
  )
    .toLowerCase()
    .startsWith("en");

  /* ======================================
     FETCH CATEGORIES
  ====================================== */

  useEffect(() => {
    dispatch(getAllCategorys());
  }, [dispatch]);

  const categories = Array.isArray(AllCategorys)
    ? AllCategorys
    : [];

  /* ======================================
     CONTENT
  ====================================== */

  const content = {
    eyebrow: isEnglish
      ? "AL NOMAN LEGAL LIBRARY"
      : "المكتبة القانونية لمؤسسة النعمان",

    title: t("Blogs"),

    description: t(
      "Discover the latest legal updates and in-depth analyses from our experts to stay informed about everything you need in the world of law."
    ),

    explore: isEnglish
      ? "Explore Categories"
      : "استكشف التصنيفات",

    imageCaption: isEnglish
      ? "Knowledge, Awareness & Legal Insights"
      : "معرفة قانونية وثقافة حقوقية",

    heroNote: isEnglish
      ? "Articles organized by legal subject."
      : "مقالات منظمة حسب الموضوع القانوني.",

    sectionEyebrow: isEnglish
      ? "EXPLORE BY SUBJECT"
      : "استكشف حسب الموضوع",

    sectionTitle: isEnglish
      ? "Explore Legal Categories"
      : "تصفح التصنيفات القانونية",

    sectionDescription: isEnglish
      ? "Choose the legal topic that interests you and discover articles covering its most important questions and principles."
      : "اختر الموضوع القانوني الذي يهمك، واطلع على مقالات تتناول أبرز المسائل والمبادئ المرتبطة به.",

    categoryCount: isEnglish
      ? "Available Categories"
      : "التصنيفات المتاحة",

    emptyTitle: isEnglish
      ? "No Categories Available"
      : "لا توجد تصنيفات متاحة",

    emptyDescription: isEnglish
      ? "Legal article categories will appear here when published."
      : "ستظهر تصنيفات المقالات القانونية هنا عند إضافتها.",
  };

  return (
    <>
      <Header active={4} DropActive={1} />

      <main className="min-h-screen overflow-hidden bg-skin-page">

        {/* ======================================
            01 — PAGE HERO
        ====================================== */}

        <section
          className="
            relative isolate overflow-hidden
            bg-[#102536]
            text-white
            dark:bg-[#081927]
          "
        >
          {/* Background atmosphere */}
          <div
            aria-hidden="true"
            className="
              pointer-events-none
              absolute -end-40 -top-48
              h-[500px] w-[500px]
              rounded-full
              bg-[#668DA5]/10
              blur-[100px]
            "
          />

          <div className="container relative z-10">

            <div
              className="
                grid items-center
                gap-12 py-14

                md:py-16

                lg:min-h-[540px]
                lg:grid-cols-2
                lg:gap-16
                lg:py-20

                xl:gap-24
              "
            >

              {/* ==================================
                  HERO TEXT
              ================================== */}

              <div className="min-w-0">

                {/* Eyebrow */}
                <div className="mb-7 flex items-center gap-3">

                  <span
                    aria-hidden="true"
                    className="h-px w-10 bg-[#D7B77F]"
                  />

                  <span className="eyebrow text-[#D7B77F]">
                    {content.eyebrow}
                  </span>
                </div>

                {/* Title */}
                <h1 className="h1 text-white">
                  {content.title}
                </h1>

                {/* Gold detail */}
                <div
                  aria-hidden="true"
                  className="
                    mt-7 h-[2px] w-16
                    rounded-full bg-[#D7B77F]
                  "
                />

                {/* Description */}
                <p
                  className="
                    body-1 mt-7
                    max-w-[620px]
                    text-[#C3D2DD]
                  "
                >
                  {content.description}
                </p>

                {/* Explore categories */}
                <a
                  href="#article-categories"
                  className="
                    group mt-9 inline-flex
                    min-h-[54px]
                    items-center justify-center
                    gap-4

                    rounded-xl
                    border border-white/20
                    bg-white/[0.09]
                    px-6 py-3

                    text-white
                    backdrop-blur-xl

                    transition-all duration-300

                    hover:border-white/40
                    hover:bg-white/[0.16]

                    focus-visible:outline-none
                    focus-visible:ring-2
                    focus-visible:ring-[#D7B77F]
                  "
                >
                  <span className="button-sm">
                    {content.explore}
                  </span>

                  <span
                    className="
                      flex h-8 w-8
                      items-center justify-center
                      rounded-lg bg-white/10
                      transition-transform duration-300

                      group-hover:translate-y-1
                    "
                  >
                    <HiOutlineArrowDown
                      size={19}
                      aria-hidden="true"
                    />
                  </span>
                </a>

                {/* Supporting detail */}
                <div
                  className="
                    mt-11 flex items-center gap-3
                    border-t border-white/10
                    pt-6
                  "
                >
                  <HiOutlineDocumentText
                    size={22}
                    aria-hidden="true"
                    className="shrink-0 text-[#D7B77F]"
                  />

                  <span className="body-3 text-white/65">
                    {content.heroNote}
                  </span>
                </div>

              </div>

              {/* ==================================
                  HERO IMAGE
              ================================== */}

              <div className="relative min-w-0 pb-5">

                {/* Decorative corner */}
                <div
                  aria-hidden="true"
                  className="
                    pointer-events-none
                    absolute -end-4 -top-4

                    h-32 w-32
                    border-e border-t
                    border-[#D7B77F]/65
                  "
                />

                {/* Main image */}
                <div
                  className="
                    group relative overflow-hidden
                    rounded-[22px]

                    border border-white/15
                    bg-[#18364A]

                    shadow-[0_25px_65px_rgba(0,0,0,0.20)]
                  "
                >
                  <img
                    src={BlogsCategoryImage}
                    alt={content.imageCaption}
                    fetchPriority="high"
                    decoding="async"
                    className="
                      aspect-[16/11]
                      w-full object-cover

                      transition-transform duration-700
                      group-hover:scale-[1.035]

                      lg:aspect-[5/4]
                    "
                  />

                  {/* Bottom image overlay */}
                  <div
                    aria-hidden="true"
                    className="
                      pointer-events-none
                      absolute inset-0
                      bg-gradient-to-t
                      from-[#061522]/65
                      via-transparent
                      to-transparent
                    "
                  />

                  {/* Image icon */}
                  <div
                    className="
                      absolute start-5 top-5
                      flex h-12 w-12
                      items-center justify-center

                      rounded-xl
                      border border-white/20
                      bg-[#102536]/65

                      text-[#D7B77F]
                      backdrop-blur-lg
                    "
                  >
                    <HiOutlineBookOpen
                      size={25}
                      aria-hidden="true"
                    />
                  </div>
                </div>

                {/* Floating caption */}
                <div
                  className="
                    relative -mt-9
                    ms-auto me-5

                    flex max-w-[85%]
                    items-center gap-3

                    rounded-xl
                    border border-white/15
                    bg-[#1D3B50]
                    px-5 py-4

                    text-white
                    shadow-[0_16px_40px_rgba(0,0,0,0.16)]
                  "
                >
                  <HiOutlineBookOpen
                    size={22}
                    aria-hidden="true"
                    className="shrink-0 text-[#D7B77F]"
                  />

                  <p className="body-3 font-semibold text-white/90">
                    {content.imageCaption}
                  </p>
                </div>

              </div>

            </div>
          </div>
        </section>

        {/* ======================================
            02 — BLOG CATEGORIES SECTION
        ====================================== */}

        <section
          id="article-categories"
          aria-labelledby="categories-heading"
          className="
            relative scroll-mt-[90px]
            overflow-hidden

            border-t border-[#DCE6ED]
            bg-[#F1F5F8]

            py-16
            sm:py-20
            lg:py-24

            dark:border-white/10
            dark:bg-[#13293B]
          "
        >
          {/* Soft background detail */}
          <div
            aria-hidden="true"
            className="
              pointer-events-none
              absolute -end-40 -top-40
              h-[440px] w-[440px]
              rounded-full

              bg-white/40 blur-3xl
              dark:bg-[#294961]/15
            "
          />

          <div className="container relative">

            {/* ==================================
                SECTION HEADER
            ================================== */}

            <div
              className="
                mb-12 flex flex-col
                justify-between gap-8

                lg:mb-14
                lg:flex-row
                lg:items-end
              "
            >
              <div className="max-w-[760px]">

                <div className="mb-5 flex items-center gap-3">

                  <span
                    aria-hidden="true"
                    className="h-px w-9 bg-skin-accent"
                  />

                  <span className="eyebrow text-skin-accent">
                    {content.sectionEyebrow}
                  </span>
                </div>

                <h2
                  id="categories-heading"
                  className="section-title text-skin-heading"
                >
                  {content.sectionTitle}
                </h2>

                <p
                  className="
                    body-2 mt-5
                    max-w-[660px]
                    text-skin-muted
                  "
                >
                  {content.sectionDescription}
                </p>
              </div>

              {/* Available categories */}
              {!isLoading && categories.length > 0 && (
                <div
                  className="
                    inline-flex shrink-0
                    items-center gap-3 self-start

                    rounded-xl
                    border border-skin-border
                    bg-skin-surface
                    px-4 py-3

                    lg:self-auto
                  "
                >
                  <HiOutlineSquares2X2
                    size={22}
                    aria-hidden="true"
                    className="text-skin-accent"
                  />

                  <span
                    className="
                      font-cairo text-lg font-bold
                      tabular-nums text-skin-heading
                    "
                  >
                    {categories.length}
                  </span>

                  <span className="body-3 text-skin-muted">
                    {content.categoryCount}
                  </span>
                </div>
              )}
            </div>

            {/* ==================================
                CATEGORIES GRID
            ================================== */}

            {isLoading ? (
              <div
                className="
                  flex min-h-[360px]
                  items-center justify-center
                "
              >
                <Loader />
              </div>
            ) : categories.length > 0 ? (
              <div
                className="
                  grid grid-cols-1
                  items-stretch gap-6

                  md:grid-cols-2
                  lg:grid-cols-3
                  lg:gap-7
                "
              >
                {categories.map((category, index) => (
                  <BlogCategoryCard
                    key={category._id || index}
                    category={category}
                    index={index}
                  />
                ))}
              </div>
            ) : (
              <div
                className="
                  flex min-h-[320px]
                  flex-col items-center
                  justify-center gap-5

                  rounded-[24px]
                  border border-skin-border
                  bg-skin-surface

                  px-6 py-12
                  text-center
                "
              >
                <div
                  className="
                    flex h-16 w-16
                    items-center justify-center

                    rounded-2xl
                    bg-skin-soft
                    text-skin-accent
                  "
                >
                  <HiOutlineBookOpen size={30} />
                </div>

                <h3 className="h5 text-skin-heading">
                  {content.emptyTitle}
                </h3>

                <p
                  className="
                    body-2 max-w-[450px]
                    text-skin-muted
                  "
                >
                  {content.emptyDescription}
                </p>
              </div>
            )}

          </div>
        </section>
      </main>

      <Fotter />
    </>
  );
}

export default BlogsCategory;
